<template>
	<div>
		<div class="mb-3 text-sm text-neutral">{{ t('customGraph.alertFlags.description') }}</div>
		<div v-if="entries.length === 0" class="rounded-xl bg-success/10 p-4 text-success">
			{{ t('customGraph.alertFlags.none') }}
		</div>
		<div v-else class="space-y-1 text-sm">
			<!-- Entries whose last value is older than two weeks (or that never had
			     one) are long-known and only clutter the list; they stay behind the
			     toggle below. -->
			<div v-if="recentEntries.length === 0" class="rounded-xl bg-success/10 p-4 text-success">
				{{ t('customGraph.alertFlags.olderOnly', { n: oldEntries.length }) }}
			</div>
			<div
				v-for="item in visibleEntries"
				:key="item.key"
				class="flex items-center justify-between rounded-xl border border-base-300 bg-base-200 px-3 py-2"
				:class="{ 'opacity-60': item.old }">
				<div class="flex min-w-0 items-center gap-1.5">
					<span class="truncate font-semibold">{{ item.label }}</span>
					<span class="truncate text-xs text-neutral">({{ item.key }})</span>
					<!-- Hidden in the graph but still configured — the stale check and
					     the alarm tab both look at it, this says why it is here. -->
					<span
						v-if="item.hidden"
						class="badge badge-ghost badge-xs gap-1 shrink-0"
						:title="t('customGraph.alertFlags.hidden')">
						<EyeOff class="h-3 w-3" />
						{{ t('customGraph.alertFlags.hidden') }}
					</span>
				</div>
				<div class="font-mono text-xs text-base-content">{{ item.lastSeenText }}</div>
			</div>
			<button v-if="oldEntries.length" type="button" class="btn btn-ghost btn-xs mt-1 gap-1" @click="showOld = !showOld">
				<component :is="showOld ? ChevronUp : ChevronDown" class="h-3 w-3" />
				{{
					showOld
						? t('customGraph.alertFlags.olderHide')
						: t('customGraph.alertFlags.olderShow', { n: oldEntries.length })
				}}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
	/**
	 * The "missing data" list of one graph: what the stale-data cron flagged in
	 * `cg.alertLogFlags` (key → last-seen unix seconds, 0 = never had a value).
	 * Shared by the Alert-Flags modal on the chart and the bell panel.
	 */
	import dayjs from 'dayjs'
	import { ChevronDown, ChevronUp, EyeOff } from 'lucide-vue-next'
	import { computed, ref } from 'vue'
	import { useI18n } from 'vue-i18n'
	import { _translations } from '@/features/chart/CustomGraphSettings/_translations'

	interface Props {
		flags: Record<string, number>
		equations?: Array<{ serial?: string; name?: string; show?: boolean }>
	}
	const props = withDefaults(defineProps<Props>(), {
		flags: () => ({}),
		equations: () => [],
	})

	const { t } = useI18n(_translations)

	/** Anything that stopped more than two weeks ago is old news and folded away. */
	const OLD_AFTER_DAYS = 14

	const entries = computed(() => {
		const eqs = props.equations || []
		const oldBefore = dayjs().subtract(OLD_AFTER_DAYS, 'day').unix()
		return Object.entries(props.flags || {})
			.map(([key, lastTs]) => {
				const eqMatch = eqs.find((eq) => eq.serial === key || eq.name === key)
				const label = eqMatch?.name || key
				const lastSeenText =
					lastTs > 0 ? dayjs.unix(lastTs).format('YYYY-MM-DD HH:mm') : t('customGraph.alertFlags.never')
				// `lastTs === 0` means "never had a value" — older than anything.
				return { key, label, lastTs, lastSeenText, hidden: eqMatch?.show === false, old: lastTs < oldBefore }
			})
			// Newest loss first, "never" at the end.
			.sort((a, b) => b.lastTs - a.lastTs)
	})
	const recentEntries = computed(() => entries.value.filter((e) => !e.old))
	const oldEntries = computed(() => entries.value.filter((e) => e.old))
	const showOld = ref(false)
	const visibleEntries = computed(() => (showOld.value ? entries.value : recentEntries.value))
</script>
